import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useMemo, useRef, useState, } from 'react';
import { useConversationStore } from '../../ConversationStoreProvider';
import { getWizardsFromConfig } from './wizardData';
const SelectionWizardContext = createContext({
    requestInlineWizard: null,
    requestEditWizard: null,
    pendingInlineWizard: null,
    pendingComposerEdit: null,
    resolvePendingInlineWizard: () => { },
    resolveComposerEditWizard: () => { },
    wizardProgress: null,
    updateWizardProgress: () => { },
    setResumedInlineToolCallId: () => { },
    markToolCallResolved: () => { },
    isToolCallResolved: () => false,
    getCompletedToolResult: () => undefined,
    clearResolvedToolCalls: () => { },
    wizardsWithSteps: [],
    wizards: [],
});
export function useSelectionWizard() {
    return useContext(SelectionWizardContext);
}
/**
 * Provider for the selectionWizard client-side tool. Pending tool calls and composer edits render
 * WizardTool in SelectionWizardComposerArea (replacing ChatInput). requestEditWizard uses the same
 * inline flow with optional initialAnswers from the last completion.
 */
export const SelectionWizardProvider = ({ children }) => {
    const store = useConversationStore();
    /** When the wizard UI is shown after localStorage reload (no live onToolCall promise). */
    const resumedInlineToolCallIdRef = useRef(null);
    const setResumedInlineToolCallId = useCallback((toolCallId) => {
        resumedInlineToolCallIdRef.current = toolCallId;
    }, []);
    const [pendingInlineWizard, setPendingInlineWizard] = useState(null);
    const [composerEditSession, setComposerEditSession] = useState(null);
    const [wizardProgress, setWizardProgress] = useState(null);
    /** Tool-call ids the user has finished this session — never reopen them. */
    const resolvedToolCallIdsRef = useRef(new Set());
    const completedToolResultsRef = useRef(new Map());
    const markToolCallResolved = useCallback((toolCallId, result) => {
        if (!toolCallId)
            return;
        resolvedToolCallIdsRef.current.add(toolCallId);
        if (result != null)
            completedToolResultsRef.current.set(toolCallId, result);
    }, []);
    const isToolCallResolved = useCallback((toolCallId) => resolvedToolCallIdsRef.current.has(toolCallId), []);
    const getCompletedToolResult = useCallback((toolCallId) => completedToolResultsRef.current.get(toolCallId), []);
    const clearResolvedToolCalls = useCallback(() => {
        resolvedToolCallIdsRef.current.clear();
        completedToolResultsRef.current.clear();
    }, []);
    const wizardsWithSteps = useMemo(() => getWizardsFromConfig(store?.clientAIConfig ?? undefined), [store?.clientAIConfig]);
    const requestInlineWizard = useCallback((toolCallId, wizardId) => {
        return new Promise((resolve) => {
            if (resolvedToolCallIdsRef.current.has(toolCallId)) {
                resolve(null);
                return;
            }
            setWizardProgress(null);
            setPendingInlineWizard({ toolCallId, wizardId, resolve });
        });
    }, []);
    const resolvePendingInlineWizard = useCallback((result) => {
        setWizardProgress(null);
        resumedInlineToolCallIdRef.current = null;
        setPendingInlineWizard((prev) => {
            if (prev) {
                markToolCallResolved(prev.toolCallId, result ?? undefined);
            }
            prev?.resolve(result);
            return null;
        });
    }, [markToolCallResolved]);
    const requestEditWizard = useCallback((wizardId, initialAnswers = {}) => {
        return new Promise((resolve) => {
            setWizardProgress(null);
            const sessionId = `edit-${wizardId}-${Date.now()}`;
            setComposerEditSession({
                sessionId,
                wizardId,
                initialAnswers: { ...initialAnswers },
                resolve,
            });
        });
    }, []);
    const resolveComposerEditWizard = useCallback((result) => {
        setWizardProgress(null);
        setComposerEditSession((prev) => {
            prev?.resolve(result);
            return null;
        });
    }, []);
    const updateWizardProgress = useCallback((toolCallId, stepIndex, totalSteps, answers) => {
        setWizardProgress((prev) => {
            const matchesInline = pendingInlineWizard && pendingInlineWizard.toolCallId === toolCallId;
            const matchesEdit = composerEditSession && composerEditSession.sessionId === toolCallId;
            const matchesResumed = resumedInlineToolCallIdRef.current === toolCallId;
            if (!matchesInline && !matchesEdit && !matchesResumed)
                return prev;
            return { toolCallId, stepIndex, totalSteps, answers: { ...answers } };
        });
    }, [pendingInlineWizard, composerEditSession]);
    const value = useMemo(() => {
        const pendingComposerEdit = composerEditSession
            ? {
                sessionId: composerEditSession.sessionId,
                wizardId: composerEditSession.wizardId,
                initialAnswers: composerEditSession.initialAnswers,
            }
            : null;
        return {
            requestInlineWizard,
            requestEditWizard,
            pendingInlineWizard: pendingInlineWizard
                ? { toolCallId: pendingInlineWizard.toolCallId, wizardId: pendingInlineWizard.wizardId }
                : null,
            pendingComposerEdit,
            resolvePendingInlineWizard,
            resolveComposerEditWizard,
            wizardProgress,
            updateWizardProgress,
            setResumedInlineToolCallId,
            markToolCallResolved,
            isToolCallResolved,
            getCompletedToolResult,
            clearResolvedToolCalls,
            wizardsWithSteps,
            wizards: wizardsWithSteps.map((w) => ({ id: w.id, name: w.name })),
        };
    }, [
        requestInlineWizard,
        requestEditWizard,
        pendingInlineWizard,
        composerEditSession,
        resolvePendingInlineWizard,
        resolveComposerEditWizard,
        wizardProgress,
        updateWizardProgress,
        setResumedInlineToolCallId,
        markToolCallResolved,
        isToolCallResolved,
        getCompletedToolResult,
        clearResolvedToolCalls,
        wizardsWithSteps,
    ]);
    return (_jsx(SelectionWizardContext.Provider, { value: value, children: children }));
};
export default SelectionWizardContext;
//# sourceMappingURL=SelectionWizardContext.js.map